import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, products } from '@/mocks/categories';

const categorySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '蔬菜分类 - 新有菜',
  description: '浏览新有菜全部蔬菜品类，包括叶菜类、根茎类、茄果类、菌菇类、豆类、瓜类等新鲜蔬菜。',
  url: 'https://www.xinyoucai.com/categories',
};

export default function CategoriesPage() {
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const activeCat = categories.find((c) => c.id === activeCategory);
  const filteredProducts = products.filter((p) => p.categoryId === activeCategory);

  return (
    <Layout>
      <SEO
        title="蔬菜分类 - 新有菜 | 新鲜蔬菜品类大全"
        description="浏览新有菜全部蔬菜品类，叶菜类、根茎类、茄果类、菌菇类、豆类、瓜类等新鲜蔬菜，产地直供，每日新鲜配送。"
        keywords="蔬菜分类,叶菜类,根茎类,茄果类,菌菇类,新鲜蔬菜品类"
        canonicalUrl="/categories"
        schema={[categorySchema]}
      />

      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '蔬菜分类' },
            ]}
          />

          <div className="mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              蔬菜分类
            </h2>
            <p className="text-sm text-foreground-500">
              六大品类，近百款新鲜蔬菜，满足您的日常所需
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 shrink-0">
              <nav className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" aria-label="分类导航">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer text-left ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-primary-600'
                    }`}
                  >
                    <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs ${
                      activeCategory === cat.id
                        ? 'bg-background-50/20'
                        : 'bg-background-100'
                    }`}>
                      <i className={cat.icon}></i>
                    </span>
                    <span>{cat.name}</span>
                    <span className={`text-xs ml-auto ${
                      activeCategory === cat.id ? 'text-background-50/70' : 'text-foreground-400'
                    }`}>
                      {cat.productCount}
                    </span>
                  </button>
                ))}
              </nav>
            </aside>

            <div className="flex-1 min-w-0">
              {activeCat && (
                <div className="mb-8 p-5 bg-background-100 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden shrink-0">
                      <img
                        src={activeCat.image}
                        alt={activeCat.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground-900 mb-1">{activeCat.name}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{activeCat.description}</p>
                      <p className="text-xs text-foreground-400 mt-2">共 {activeCat.productCount} 款产品</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
                {filteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 transition-all duration-300 hover:border-primary-200 cursor-pointer"
                    style={{ animation: `slideUp 300ms ease-out ${index * 50}ms both` }}
                    onMouseEnter={() => setHoveredId(product.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div className="aspect-square overflow-hidden relative">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {product.isNew && (
                          <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full">
                            新品
                          </span>
                        )}
                        {product.isHot && (
                          <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
                            热卖
                          </span>
                        )}
                      </div>
                      {hoveredId === product.id && (
                        <div className="absolute inset-0 bg-black/10 flex items-center justify-center animate-[fadeIn_200ms_ease-out]">
                          <span className="px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full">
                            查看详情
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-3 md:p-4">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">{product.name}</h4>
                      <p className="text-xs text-foreground-400 mb-2">{product.subtitle}</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-base font-bold text-accent-600">¥{product.price}</span>
                          <span className="text-xs text-foreground-400 ml-1">/{product.unit}</span>
                        </div>
                        <span className="text-xs text-foreground-400">{product.origin}</span>
                      </div>
                      {product.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {product.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-secondary-50 text-secondary-700 text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {filteredProducts.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无产品</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}